import React from "react";
import { IconX, IconMessageCircle, IconPhone } from "@tabler/icons-react";
import MoonLoader from "react-spinners/MoonLoader";
import PerfectScrollbar from "react-perfect-scrollbar";
import ConversationFilters from "./ConversationFilters";
import ConversationList from "./ConversationList";
import ConversationItem from "./ConversationItem";
import ChannelFilters from "./ChannelFilters";
import { useConversations } from "../../hooks";
import { useServiceCenter } from "../../contexts";
import { phoneToConversation } from "../../utils/conversationAdapter";
import { normalizePhone, formatPhone } from "../../utils/formatters";
import { Hash } from "../../../../helpers/Helpers";
import { AppSettings } from "../../../../App";
import "./ConversationSidebar.css";

/**
 * Sidebar completa de conversas
 */
const ConversationSidebar = ({ showGrouped = true }) => {
  const { selectedAccount, accountProfilePicture, unreadCounts } = useServiceCenter();

  const {
    groupedConversations,
    statusCounts,
    selectedConversation,
    hasMoreConversations,
    isLoading,
    filters,
    selectConversation,
    loadMoreConversations,
    setSearchFilter,
    setStatusFilter,
    setDepartmentFilter,
    // Busca server-side (Camada 1 + Camada 2)
    searchResults,
    isSearching,
    searchActive,
    searchQuery,
    searchContactsServer,
    clearSearch,
  } = useConversations();

  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    await loadMoreConversations();
    setIsLoadingMore(false);
  };

  const handleResetFilters = () => {
    setSearchFilter("");
    setStatusFilter("all");
    setDepartmentFilter(null);
    clearSearch();
  };

  const handleSearchSubmit = (q) => {
    searchContactsServer(q);
  };

  const handleClearSearch = () => {
    setSearchFilter("");
    clearSearch();
  };

  // Camada 2 — fallback "Abrir chat com {phone}" quando a query é só
  // dígitos (telefone) e a busca server-side não retornou resultados.
  // O ChatWindow + useMessages.loadMessages cuida do resto: chama
  // GET /chats/{phone}@c.us/messages no backend, que roteia pra WAHA
  // (ou Cloud API se for o caso).
  const phoneFallbackDigits = React.useMemo(() => {
    const q = (searchQuery || "").replace(/\D/g, "");
    return q.length >= 10 ? q : null;
  }, [searchQuery]);

  const handleOpenPhone = () => {
    if (!phoneFallbackDigits) return;
    const conv = phoneToConversation(phoneFallbackDigits);
    if (conv) selectConversation(conv);
  };

  // Avatar da conta
  const accountAvatar =
    accountProfilePicture ||
    (AppSettings?.logo
      ? Hash("250x250", AppSettings.logo)
      : `https://ui-avatars.com/api/?name=${
          selectedAccount?.phone || AppSettings?.name || "WA"
        }&background=random`);

  return (
    <div className="sc-sidebar">
      {/* Header omnichannel: chips por canal (sem account selector) */}
      <ChannelFilters />

      {/* Filtros (busca, status, departamento) */}
      <ConversationFilters
        filters={filters}
        statusCounts={statusCounts}
        onSearchChange={setSearchFilter}
        onSearchSubmit={handleSearchSubmit}
        onStatusChange={setStatusFilter}
        onDepartmentChange={setDepartmentFilter}
        onReset={handleResetFilters}
        searchActive={searchActive}
        isSearching={isSearching}
      />

      {/* Modo busca server-side: mostra resultados em vez da lista normal */}
      {searchActive ? (
        <div className="sc-conversation-list sc-search-results">
          <div className="sc-search-results__banner">
            <span className="sc-search-results__label">
              {isSearching
                ? "Buscando..."
                : `${searchResults.length} ${
                    searchResults.length === 1 ? "resultado" : "resultados"
                  } para "${searchQuery}"`}
            </span>
            <button
              className="sc-search-results__close"
              onClick={handleClearSearch}
              title="Fechar busca"
            >
              <IconX size={14} />
              Voltar
            </button>
          </div>

          <PerfectScrollbar>
            {isSearching ? (
              <div className="sc-conversation-list__loading">
                <MoonLoader size={26} color="#3b82f6" />
                <span>Buscando no histórico...</span>
              </div>
            ) : searchResults.length > 0 ? (
              searchResults.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isSelected={selectedConversation?.id === conversation.id}
                  unreadCount={
                    unreadCounts[normalizePhone(conversation.phone)] || 0
                  }
                  onClick={() => selectConversation(conversation)}
                />
              ))
            ) : (
              <div className="sc-conversation-list__empty">
                <span className="sc-conversation-list__empty-icon">
                  <IconMessageCircle size={36} />
                </span>
                <span>Nenhum contato encontrado no histórico</span>
              </div>
            )}

            {/* Camada 2: fallback "Abrir chat com {phone}" */}
            {!isSearching && phoneFallbackDigits && (
              <div className="sc-search-results__phone-fallback">
                <div className="sc-search-results__fallback-hint">
                  Não está no histórico? Tentar abrir direto pelo número:
                </div>
                <button
                  className="sc-search-results__fallback-btn"
                  onClick={handleOpenPhone}
                >
                  <IconPhone size={16} />
                  Abrir chat com {formatPhone(phoneFallbackDigits)}
                </button>
              </div>
            )}
          </PerfectScrollbar>
        </div>
      ) : (
        <ConversationList
          groupedConversations={groupedConversations}
          selectedConversation={selectedConversation}
          unreadCounts={unreadCounts}
          onSelectConversation={selectConversation}
          isLoading={isLoading}
          hasMore={hasMoreConversations}
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
          showGrouped={showGrouped}
        />
      )}
    </div>
  );
};

export default ConversationSidebar;
